<script setup lang="ts">
import { EEmptyState, toProblemDetails } from '@erpx/ui'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { problemText } from '@/i18n/problem'

/**
 * The failure surface for a query.
 *
 * §11: "Errors are human + retry." The server sends RFC 9457 problem+json with
 * a `detail` written for a person, so that is what is shown — never the raw
 * body, never a bare status code. The trace id is offered quietly underneath
 * because it is what turns a support ticket into a one-minute log lookup.
 */

const props = withDefaults(
  defineProps<{
    error: unknown
    title?: string
    retryLabel?: string
  }>(),
  { title: '', retryLabel: '' },
)

const emit = defineEmits<{ retry: [] }>()

const { t } = useI18n()

const heading = computed(() => (props.title === '' ? t('errors.loadTitle') : props.title))
const retry = computed(() => (props.retryLabel === '' ? t('common.actions.retry') : props.retryLabel))
const message = computed(() => problemText(props.error))
const traceId = computed(() => toProblemDetails(props.error)?.traceId ?? '')
</script>

<template>
  <EEmptyState
    variant="error"
    :title="heading"
    :description="message"
    :action-label="retry"
    @action="emit('retry')"
  >
    <template v-if="traceId !== ''" #description>
      <span>{{ message }}</span>
      <br />
      <span class="muted text-xs">{{ $t('errors.reference', { traceId }) }}</span>
    </template>
  </EEmptyState>
</template>
